import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

export default function EmailCampaignTourDialog({
  open,
  onClose,
  onDismiss,
  onFinish,
  title = 'Quick Tour',
  steps = [],
}) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open]);

  const total = Math.max(1, steps.length);
  const current = useMemo(() => steps[stepIndex] || {}, [steps, stepIndex]);
  const progress = ((stepIndex + 1) / total) * 100;
  const isLast = stepIndex >= total - 1;

  // Closing the tour (Done, Close, backdrop, Escape) always marks it as seen so
  // it never auto-pops again — the page handlers persist that. Re-open anytime
  // via the "Tour" button.
  const handleDismiss = () => {
    if (typeof onDismiss === 'function') {
      onDismiss();
      return;
    }
    onClose?.();
  };

  const handleFinish = () => {
    onFinish?.();
  };

  return (
    <Dialog open={open} onClose={handleDismiss} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">
            Step {stepIndex + 1} of {total}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {current.title || 'Overview'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current.description || ''}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss}>Close</Button>
        <Button onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))} disabled={stepIndex === 0}>
          Back
        </Button>
        {isLast ? (
          <Button variant="contained" onClick={handleFinish}>
            Done
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setStepIndex((prev) => Math.min(total - 1, prev + 1))}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
